// Lịch Sử Học Tập Page
// Hiển thị lịch sử học bài, làm quiz, và thành tích

import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { studyHistoryService } from '../services/studyHistory/StudyHistoryService';
import { quizHistoryService } from '../services/studyHistory/QuizHistoryService';
import { achievementService } from '../services/studyHistory/AchievementService';
import {
  StudyProgress,
  QuizResult,
  Achievement,
  UserStatistics
} from '../types/learningHistory';
import Navigation from '../components/common/Navigation';
import Footer from '../components/common/Footer';
import {
  BookOpen,
  Award,
  TrendingUp,
  Clock,
  Target,
  BarChart3,
  Calendar,
  Trophy,
  Flame
} from 'lucide-react';

export default function LichSuHocTap() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'lessons' | 'quizzes' | 'achievements'>('overview');
  
  // Data states
  const [statistics, setStatistics] = useState<UserStatistics | null>(null);
  const [lessonProgress, setLessonProgress] = useState<StudyProgress[]>([]);
  const [quizHistory, setQuizHistory] = useState<QuizResult[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    loadAllData();
  }, [user?.id]);

  const loadAllData = async () => {
    if (!user?.id) return;

    setLoading(true);

    try {
      console.log('📊 Loading study history data...');

      // Load all data in parallel
      const [stats, lessons, quizzes, achievs] = await Promise.all([
        studyHistoryService.getUserStatistics(user.id),
        studyHistoryService.getAllLessonProgress(user.id),
        quizHistoryService.getQuizHistory(user.id, { limit: 50 }),
        achievementService.getUserAchievements(user.id)
      ]);

      setStatistics(stats);
      setLessonProgress(lessons);
      setQuizHistory(quizzes);
      setAchievements(achievs);

      console.log('✅ Study history data loaded:', { stats, lessons: lessons.length, quizzes: quizzes.length, achievements: achievs.length });
    } catch (error) {
      console.error('❌ Failed to load study history:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4">Vui lòng đăng nhập để xem lịch sử học tập</p>
          <a href="/dang-nhap" className="btn-primary px-6 py-3 rounded-lg inline-block">
            Đăng nhập
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải lịch sử học tập...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                📚 Lịch Sử Học Tập
              </h1>
              <p className="text-gray-600">
                Theo dõi tiến độ học tập và thành tích của bạn
              </p>
            </div>
            {statistics && (
              <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-lg">
                <Flame className="w-5 h-5 text-orange-500" />
                <span className="font-bold text-orange-600">
                  {statistics.currentStreak} ngày
                </span>
              </div>
            )}
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2 border-b">
            {[
              { key: 'overview', label: 'Tổng quan', icon: BarChart3 },
              { key: 'lessons', label: 'Bài học', icon: BookOpen },
              { key: 'quizzes', label: 'Trắc nghiệm', icon: Target },
              { key: 'achievements', label: 'Thành tích', icon: Trophy }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex items-center gap-2 px-6 py-3 border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600 font-semibold'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && statistics && (
            <OverviewTab statistics={statistics} />
          )}

          {activeTab === 'lessons' && (
            <LessonsTab lessons={lessonProgress} />
          )}

          {activeTab === 'quizzes' && (
            <QuizzesTab quizzes={quizHistory} />
          )}

          {activeTab === 'achievements' && (
            <AchievementsTab achievements={achievements} />
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

// Helper function to format time in seconds to readable format
function formatStudyTime(seconds: number): { display: string; detail: string } {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  // Display format (for large number)
  let display = '';
  if (hours > 0) {
    display = `${hours}h`;
  } else if (minutes > 0) {
    display = `${minutes}p`;
  } else {
    display = `${secs}s`;
  }
  
  // Detail format (full breakdown)
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}p`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
  
  return {
    display,
    detail: parts.join(' ')
  };
}

// Overview Tab Component
function OverviewTab({ statistics }: { statistics: UserStatistics }) {
  const studyTime = formatStudyTime(statistics.totalStudyTime);
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {/* Total Lessons */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <BookOpen className="w-8 h-8 text-blue-500" />
          <span className="text-3xl font-bold text-gray-800">
            {statistics.completedLessons}
          </span>
        </div>
        <h3 className="text-gray-600 text-sm">Bài học hoàn thành</h3>
        <p className="text-xs text-gray-500 mt-1">
          / {statistics.totalLessons} bài
        </p>
      </div>

      {/* Total Quizzes */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <Target className="w-8 h-8 text-green-500" />
          <span className="text-3xl font-bold text-gray-800">
            {statistics.totalQuizzes}
          </span>
        </div>
        <h3 className="text-gray-600 text-sm">Bài trắc nghiệm</h3>
        <p className="text-xs text-gray-500 mt-1">
          Điểm TB: {statistics.averageScore.toFixed(1)}
        </p>
      </div>

      {/* Study Time */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <Clock className="w-8 h-8 text-purple-500" />
          <span className="text-3xl font-bold text-gray-800">
            {studyTime.display}
          </span>
        </div>
        <h3 className="text-gray-600 text-sm">Thời gian học</h3>
        <p className="text-xs text-gray-500 mt-1">
          {studyTime.detail}
        </p>
      </div>

      {/* Achievements */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <Trophy className="w-8 h-8 text-yellow-500" />
          <span className="text-3xl font-bold text-gray-800">
            {statistics.achievements.length}
          </span>
        </div>
        <h3 className="text-gray-600 text-sm">Huy hiệu</h3>
        <p className="text-xs text-gray-500 mt-1">
          Thành tích đạt được
        </p>
      </div>

      {/* Score Distribution */}
      <div className="md:col-span-2 lg:col-span-4 bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Phân bố điểm số
        </h3>
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {statistics.scoreDistribution.excellent}
            </div>
            <div className="text-sm text-gray-600">Xuất sắc</div>
            <div className="text-xs text-gray-500">(90-100)</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {statistics.scoreDistribution.good}
            </div>
            <div className="text-sm text-gray-600">Khá</div>
            <div className="text-xs text-gray-500">(70-89)</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {statistics.scoreDistribution.fair}
            </div>
            <div className="text-sm text-gray-600">Trung bình</div>
            <div className="text-xs text-gray-500">(50-69)</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {statistics.scoreDistribution.poor}
            </div>
            <div className="text-sm text-gray-600">Yếu</div>
            <div className="text-xs text-gray-500">(0-49)</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Lessons Tab Component
function LessonsTab({ lessons }: { lessons: StudyProgress[] }) {
  if (lessons.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-12 text-center">
        <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Chưa có bài học nào</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Bài học</th>
              <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Tiến độ</th>
              <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Điểm Tương Tác</th>
              <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Thời gian</th>
              <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Trạng thái</th>
              <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Cập nhật</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {lessons.map(lesson => (
              <tr key={lesson.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-800">{lesson.lessonTitle}</div>
                  <div className="text-sm text-gray-500">{lesson.lessonId}</div>
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${lesson.progress}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold">{lesson.progress}%</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  {lesson.interactiveStats ? (
                    <div>
                      <div className={`text-2xl font-bold ${
                        lesson.interactiveStats.score >= 90 ? 'text-green-600' :
                        lesson.interactiveStats.score >= 70 ? 'text-blue-600' :
                        lesson.interactiveStats.score >= 50 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {lesson.interactiveStats.score}
                      </div>
                      <div className="text-xs text-gray-500 space-y-0.5">
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-green-600">✓ {lesson.interactiveStats.correctAnswers} đúng</span>
                          <span>•</span>
                          <span className="text-red-600">✗ {lesson.interactiveStats.wrongAnswers} sai</span>
                        </div>
                        <div className="text-gray-400">
                          Tổng: {lesson.interactiveStats.totalQuestions} câu
                        </div>
                      </div>
                      <div className="flex items-center justify-center gap-3 mt-1.5 text-xs">
                        <span className={lesson.interactiveStats.hintsUsed > 0 ? "text-yellow-600 font-semibold" : "text-gray-400"}>
                          💡 {lesson.interactiveStats.hintsUsed} gợi ý
                        </span>
                        <span className={lesson.interactiveStats.aiQuestionsAsked > 0 ? "text-purple-600 font-semibold" : "text-gray-400"}>
                          🤖 {lesson.interactiveStats.aiQuestionsAsked} lần AI
                        </span>
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">Chưa có</span>
                  )}
                </td>
                <td className="px-6 py-4 text-center text-sm text-gray-600">
                  {formatStudyTime(lesson.timeSpent).detail}
                </td>
                <td className="px-6 py-4 text-center">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    lesson.status === 'completed' ? 'bg-green-100 text-green-800' :
                    lesson.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {lesson.status === 'completed' ? 'Hoàn thành' :
                     lesson.status === 'in_progress' ? 'Đang học' : 'Chưa bắt đầu'}
                  </span>
                </td>
                <td className="px-6 py-4 text-center text-sm text-gray-500">
                  {new Date(lesson.lastAccessedAt).toLocaleDateString('vi-VN')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Quizzes Tab Component
function QuizzesTab({ quizzes }: { quizzes: QuizResult[] }) {
  if (quizzes.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-12 text-center">
        <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Chưa có bài trắc nghiệm nào</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {quizzes.map(quiz => (
        <div key={quiz.id} className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-800">{quiz.quizTitle}</h3>
              <p className="text-sm text-gray-500">Lần {quiz.attemptNumber}</p>
            </div>
            <div className={`text-3xl font-bold ${
              quiz.passed ? 'text-green-600' : 'text-red-600'
            }`}>
              {quiz.score}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            <div>
              <div className="text-sm text-gray-600">Đúng</div>
              <div className="text-xl font-bold text-green-600">{quiz.correctAnswers}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Sai</div>
              <div className="text-xl font-bold text-red-600">{quiz.wrongAnswers}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Bỏ qua</div>
              <div className="text-xl font-bold text-gray-600">{quiz.skippedAnswers}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 flex items-center gap-1">
                <span>⚠️ Thoát</span>
              </div>
              <div className={`text-xl font-bold ${
                (quiz.fullscreenExitCount || 0) === 0 ? 'text-green-600' :
                (quiz.fullscreenExitCount || 0) <= 2 ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {quiz.fullscreenExitCount || 0}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Thời gian</div>
              <div className="text-xl font-bold text-blue-600">
                {Math.floor(quiz.timeSpent / 60)}:{(quiz.timeSpent % 60).toString().padStart(2, '0')}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              quiz.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {quiz.passed ? 'Đạt' : 'Chưa đạt'}
            </span>
            <span className="text-sm text-gray-500">
              {new Date(quiz.createdAt).toLocaleString('vi-VN')}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// Achievements Tab Component
function AchievementsTab({ achievements }: { achievements: Achievement[] }) {
  if (achievements.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-12 text-center">
        <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Chưa có thành tích nào</p>
        <p className="text-sm text-gray-500 mt-2">
          Tiếp tục học tập để mở khóa huy hiệu!
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {achievements.map(achievement => (
        <div key={achievement.id} className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl shadow-lg p-6 border-2 border-yellow-200">
          <div className="text-5xl mb-4 text-center">{achievement.icon}</div>
          <h3 className="text-lg font-bold text-gray-800 text-center mb-2">
            {achievement.title}
          </h3>
          <p className="text-sm text-gray-600 text-center mb-4">
            {achievement.description}
          </p>
          <div className="text-xs text-gray-500 text-center">
            {new Date(achievement.earnedAt).toLocaleDateString('vi-VN')}
          </div>
        </div>
      ))}
    </div>
  );
}

